import React from 'react';
import { Form, InputNumber, Typography } from 'antd';

import type { FormInstance } from 'antd';
import { UNIT_CREDIT_PRICE, currencyOptions } from 'src/constants/unitPrice';

const { Text } = Typography;

// Core types
type ProviderKey = 'deepgram' | 'cartesia' | '11labs' | 'transferCall';

interface Provider {
	label: string;
	field: ProviderKey;
	description?: string;
	basePrice?: number;
}

interface MeterRateConfigProps {
	form: FormInstance;
	currency: string;
	exchangeRates?: Record<string, number>;
}

// Provider configuration
const VOICE_PROVIDERS: Record<ProviderKey, Provider> = {
	deepgram: {
		label: 'Deepgram Voice',
		field: 'deepgram',
		description: 'Price You Pay: 1 credit/min (where 1 credit = $0.12)',
		basePrice: 0.12,
	},
	cartesia: {
		label: 'Cartesia Voice',
		field: 'cartesia',
		description: 'Price You Pay: 1 credit/min (where 1 credit = $0.12)',
		basePrice: 0.12,
	},
	'11labs': {
		label: '11Labs Voice',
		field: '11labs',
		description: 'Price You Pay: 1 credit/min (where 1 credit = $0.12)',
		basePrice: 0.12,
	},
	transferCall: {
		label: 'Transfer Call',
		field: 'transferCall',
		description: 'Price You Pay: 0.25 credit/min (~$0.03)',
		basePrice: 0.03,
	},
};

const MeterRateConfig: React.FC<MeterRateConfigProps> = ({
	form,
	currency,
	exchangeRates = {},
}) => {
	// Function to convert price to USD (same as in ResellerPlansCreation)
	const convertToUSD = (price: number, fromCurrency: string): number => {
		if (fromCurrency === 'usd') {
			return price;
		}
		const rate = exchangeRates[fromCurrency.toUpperCase()];
		return rate ? price / rate : price;
	};

	const calculateMargin = (chargePrice: number, basePrice: number | undefined) => {
		if (chargePrice === 0 || !basePrice) {
			return { text: '(Free)', margin: 0 };
		}

		// Convert charge price to USD for accurate margin calculation
		const chargePriceInUSD = convertToUSD(chargePrice, currency);
		const marginDollars = chargePriceInUSD - basePrice;

		return {
			text:
				marginDollars >= 0
					? `(+$${marginDollars.toFixed(2)}/min profit)`
					: `(-$${Math.abs(marginDollars).toFixed(2)}/min loss)`,
			margin: marginDollars,
		};
	};

	// Handle individual field updates
	const updateFieldValue = (provider: ProviderKey, value: number | null) => {
		// Never allow null values - default to 0 if null or undefined
		const safeValue = value === null || value === undefined ? 0 : value;

		// Get current form values
		const currentValues = form.getFieldValue('meterPrices') || {};

		// Update form with all values intact except the changed one
		form.setFieldsValue({
			meterPrices: {
				...currentValues,
				[provider]: safeValue,
			},
		});
	};

	// Get currency symbol
	const getCurrencySymbol = () => {
		return currencyOptions.find((opt) => opt.value === currency)?.symbol || '$';
	};

	return (
		<div className="space-y-6">
			<div className="space-y-4">
				<Text type="secondary">
					Set your pricing for each voice provider. Enter the price per minute in{' '}
					{currency.toUpperCase()}. The base cost we charge you is shown for reference. The
					difference is your margin.
				</Text>

				<Form.Item name="meterPrices" initialValue={{}} hidden>
					<input type="hidden" />
				</Form.Item>

				<div className="space-y-4">
					{(Object.entries(VOICE_PROVIDERS) as [ProviderKey, Provider][]).map(([key, provider]) => (
						<div key={key}>
							<div>
								<Text strong>{provider.label}</Text>
								<Text type="secondary" className="block text-xs">
									{provider.description}
								</Text>
							</div>
							<div className="flex items-center gap-4 mt-2">
								<div className="flex-1 relative">
									<Form.Item noStyle shouldUpdate>
										{() => {
											const allValues = form.getFieldValue('meterPrices') || {};
											const dollarValue = allValues[provider.field] || 0;

											return (
												<div className="w-full">
													<InputNumber
														className="w-full"
														min={0}
														max={1}
														step={0.1}
														precision={2}
														value={dollarValue}
														prefix={getCurrencySymbol()}
														addonAfter={`per min (${currency.toUpperCase()})`}
														onChange={(value) => updateFieldValue(provider.field, value)}
														placeholder={`Enter price in ${currency.toUpperCase()}`}
														stringMode={false}
														controls={true}
														onBlur={(e) => {
															const inputValue = parseFloat(e.target.value);
															if (isNaN(inputValue)) {
																updateFieldValue(provider.field, 0);
															}
														}}
													/>
													<Text type="secondary" className="text-xs mt-1 block">
														credits: {Math.round(dollarValue / UNIT_CREDIT_PRICE)}
													</Text>
												</div>
											);
										}}
									</Form.Item>
								</div>
								<div className="w-48 text-right">
									<Form.Item noStyle shouldUpdate>
										{() => {
											const allValues = form.getFieldValue('meterPrices') || {};
											const dollarValue = allValues[provider.field] || 0;
											const basePrice = provider.basePrice || 0;

											return (
												<div>
													<Text strong>
														{getCurrencySymbol()}
														{dollarValue.toFixed(2)}
													</Text>
													<Text
														className="ml-2"
														type={dollarValue === 0 ? 'secondary' : undefined}
														style={{
															color:
																dollarValue === 0
																	? undefined
																	: calculateMargin(dollarValue, basePrice).margin > 0
																		? '#52c41a'
																		: '#ff4d4f',
														}}
													>
														{calculateMargin(dollarValue, basePrice).text}
													</Text>
												</div>
											);
										}}
									</Form.Item>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default MeterRateConfig;
